import { useEffect, useState } from 'react'
import { api } from '../../../lib/api.js'
import { useAuth } from '../../../lib/auth.jsx'
import { toast } from 'sonner'

const API_BASE = '/admin/users'

export function useUsers() {
  const { user: me } = useAuth()
  const [users, setUsers] = useState([])
  const [pagination, setPagination] = useState(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')

  const load = async (page = 1) => {
    setLoading(true)
    try {
      const params = { page }
      if (search) params.search = search
      const { data } = await api.get(API_BASE, { params })
      setUsers(data.data.data || data.data)
      setPagination(data.data)
    } finally { setLoading(false) }
  }

  useEffect(() => { load(1) }, [])

  // ── Security: cannot delete or deactivate self ──
  const isSelf = (u) => u?.id === me?.id

  const remove = async (u) => {
    if (isSelf(u)) return toast.error('Tidak bisa menghapus akun sendiri')
    if (!confirm(`Hapus pengguna "${u.name}"?`)) return
    try {
      await api.delete(`${API_BASE}/${u.id}`)
      toast.success('Berhasil dihapus')
      load()
    } catch (e) { toast.error(e.response?.data?.message || 'Gagal menghapus') }
  }

  // Optimistic toggle active — flip locally, sync server state, revert on error
  const toggleActive = async (u) => {
    if (isSelf(u)) return toast.error('Tidak bisa menonaktifkan akun sendiri')
    setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, is_active: !x.is_active } : x))
    try {
      const { data } = await api.patch(`${API_BASE}/${u.id}/toggle-active`)
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, ...data.data } : x))
    } catch (e) {
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, is_active: u.is_active } : x))
      toast.error(e.response?.data?.message || 'Gagal mengubah status')
    }
  }

  const save = async (form) => {
    const payload = {
      name: form.name,
      email: form.email,
      is_active: form.is_active,
      roles: form.roles,
    }
    if (form.password) {
      payload.password = form.password
      payload.password_confirmation = form.password_confirmation
    }

    try {
      if (form.isNew) {
        await api.post(API_BASE, payload)
        toast.success('Pengguna berhasil dibuat')
        load()
      } else {
        await api.patch(`${API_BASE}/${form.id}`, payload)
        toast.success('Pengguna berhasil diperbarui')
        // Optimistic: update local state for edits
        setUsers((prev) => prev.map((x) => x.id === form.id ? { ...x, name: form.name, email: form.email, is_active: form.is_active, roles: form.roles } : x))
      }
    } catch (e) {
      if (form.isNew) load() // Revert only if new user (no local state to revert)
      toast.error(e.response?.data?.message || 'Gagal menyimpan')
    }
  }

  return {
    me, users, loading, pagination, search, setSearch,
    load, remove, toggleActive, save,
  }
}
